import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Briefcase, ChevronRight } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${window.location.origin}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

// Sample jobs for display
const sampleJobs = [
  {
    job_id: 'sample1',
    title: 'Senior Event Coordinator',
    department: 'Operations',
    location: 'Los Angeles, CA',
    type: 'Full-time',
    description: 'We are seeking an experienced Event Coordinator to join our growing team. The ideal candidate will have a passion for creating memorable experiences and excellent organizational skills.',
    requirements: [
      '5+ years of event planning experience',
      'Strong vendor relationship management',
      'Excellent communication skills',
      'Bachelor\'s degree in Event Management or related field'
    ],
    responsibilities: [
      'Coordinate all aspects of event planning and execution',
      'Manage client relationships and expectations',
      'Negotiate contracts with vendors',
      'Create and manage event budgets'
    ]
  },
  {
    job_id: 'sample2',
    title: 'Creative Director',
    department: 'Design',
    location: 'Los Angeles, CA',
    type: 'Full-time',
    description: 'Lead our design team in creating stunning visual experiences for luxury events. This role requires a visionary who can translate client dreams into reality.',
    requirements: [
      '7+ years of design experience',
      'Proficiency in Adobe Creative Suite',
      'Portfolio demonstrating event design work',
      'Leadership experience'
    ],
    responsibilities: [
      'Lead creative vision for all event designs',
      'Manage and mentor design team',
      'Present concepts to clients',
      'Ensure brand consistency across all touchpoints'
    ]
  },
  {
    job_id: 'sample3',
    title: 'Event Design Assistant',
    department: 'Design',
    location: 'Remote',
    type: 'Part-time',
    description: 'Support our design team in bringing event visions to life. Perfect for someone looking to grow their career in luxury event design.',
    requirements: [
      '1-2 years of design experience',
      'Knowledge of design software',
      'Strong attention to detail',
      'Passion for events and design'
    ],
    responsibilities: [
      'Assist with mood board creation',
      'Source materials and decor items',
      'Support senior designers on projects',
      'Maintain design asset library'
    ]
  }
];

export const Jobs = () => {
  const [jobs, setJobs] = useState(sampleJobs);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${API}/jobs`);
        if (response.data.length > 0) {
          setJobs(response.data);
        }
      } catch (error) {
        // Falls back to sample jobs already in state
        console.warn('Failed to load jobs:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="jobs-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Careers</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Join Our<br />
              <span className="text-gold-gradient">Exceptional Team</span>
            </h1>
            <p className="text-white/60 text-lg leading-relaxed">
              Be part of a team that creates unforgettable experiences. We&apos;re always looking for passionate individuals who share our commitment to excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Jobs Listing */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]" data-testid="jobs-listing">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="font-heading text-3xl text-white">Open Positions</h2>
            <p className="text-white/60 mt-2">{jobs.length} opportunities available</p>
          </motion.div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-dark p-6 rounded-sm animate-pulse">
                  <div className="h-6 bg-white/10 rounded w-1/3 mb-4" />
                  <div className="h-4 bg-white/10 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.job_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group card-dark p-6 rounded-sm cursor-pointer hover:bg-[#141414] transition-colors"
                  onClick={() => setSelectedJob(job)}
                  data-testid={`job-card-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-xl text-white group-hover:text-[#D4AF37] transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 mt-3 text-white/60 text-sm">
                        <span className="flex items-center gap-1.5">
                          <Briefcase size={14} className="text-[#D4AF37]" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <MapPin size={14} className="text-[#D4AF37]" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock size={14} className="text-[#D4AF37]" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="culture-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Our Culture</span>
              <h2 className="font-heading text-4xl text-white mt-4 mb-6">
                Why Work With Us
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-white font-medium mb-2">Creative Freedom</h3>
                  <p className="text-white/60 text-sm">Express your creativity and bring bold ideas to life in a supportive environment.</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Growth Opportunities</h3>
                  <p className="text-white/60 text-sm">Continuous learning and career development are core to our culture.</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Work-Life Balance</h3>
                  <p className="text-white/60 text-sm">We believe in flexible arrangements and respecting personal time.</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Collaborative Environment</h3>
                  <p className="text-white/60 text-sm">Work alongside talented professionals who share your passion for excellence.</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.pexels.com/photos/36581576/pexels-photo-36581576.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Our Team"
                className="w-full h-[500px] object-cover rounded-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Job Detail Modal */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">
                  {selectedJob.title}
                </DialogTitle>
                <div className="flex flex-wrap gap-4 mt-3 text-white/60 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Briefcase size={14} className="text-[#D4AF37]" />
                    {selectedJob.department}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    {selectedJob.location}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={14} className="text-[#D4AF37]" />
                    {selectedJob.type}
                  </span>
                </div>
              </DialogHeader>
              
              <div className="mt-6 space-y-6">
                <div>
                  <h4 className="text-white font-medium mb-2">About the Role</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{selectedJob.description}</p>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">Requirements</h4>
                  <ul className="space-y-2">
                    {selectedJob.requirements.map((req) => (
                      <li key={req} className="text-white/60 text-sm flex items-start gap-2">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">Responsibilities</h4>
                  <ul className="space-y-2">
                    {selectedJob.responsibilities.map((resp) => (
                      <li key={resp} className="text-white/60 text-sm flex items-start gap-2">
                        <span className="text-[#D4AF37] mt-1">•</span>
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <a
                  href={`mailto:careers@whiteglove.events?subject=Application for ${selectedJob.title}`}
                  className="btn-primary w-full text-center block"
                  data-testid="apply-btn"
                >
                  Apply Now
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
